import type { Rede } from '../components/charts/coresPlataforma'
import { supabase } from '../lib/supabase'
import { periodoAnterior, type Intervalo } from '../utils/periodo'

export type { Rede }

export const REDES: Array<{ id: Rede; nome: string }> = [
  { id: 'instagram', nome: 'Instagram' },
  { id: 'facebook', nome: 'Facebook' },
  { id: 'tiktok', nome: 'TikTok' },
]

export const nomeRede = (r: string) => REDES.find((x) => x.id === r)?.nome ?? r

export type FiltroSocial = {
  intervalo: Intervalo
  /** '' = todas as marcas visíveis */
  marcaId: string
  /** '' = as três redes */
  rede: Rede | ''
}

export type MetricasDia = {
  novosSeguidores: number
  alcance: number
  visualizacoes: number
  interacoes: number
  visitasPerfil: number
  cliquesLink: number
}

export type DiaSocial = MetricasDia & { data: string; rede: Rede; demo: boolean }

export type TotaisSocial = Omit<MetricasDia, 'alcance'> & {
  /** foto do fim do período, somada entre as contas — não é soma de dias */
  seguidores: number
  publicacoes: number
  demo: boolean
}

/** O que as redes levaram ao site, pelo GA4 (canal Organic Social). */
export type SiteVindoDasRedes = { sessoes: number; conversoes: number }

export type Publicacao = {
  id: string
  rede: Rede
  marcaId: string
  marca: string
  tipo: string | null
  publicadaEm: string
  legenda: string | null
  link: string | null
  /** URL pública da cópia da miniatura (Storage); null se ainda não há. */
  miniatura: string | null
  visualizacoes: number
  interacoes: number
}

export type DadosSocial = {
  serie: DiaSocial[]
  atual: TotaisSocial
  anterior: TotaisSocial
  site: SiteVindoDasRedes
  siteAnterior: SiteVindoDasRedes
  publicacoes: Publicacao[]
}

type Bruto = Record<string, unknown>
const num = (v: unknown) => Number(v ?? 0)

async function rpc(fn: string, args: Record<string, unknown>): Promise<Bruto[]> {
  const { data, error } = await supabase.rpc(fn, args)
  if (error) throw new Error(error.message)
  return (data ?? []) as Bruto[]
}

const totais = (r: Bruto = {}): TotaisSocial => ({
  seguidores: num(r.seguidores),
  novosSeguidores: num(r.novos_seguidores),
  visualizacoes: num(r.visualizacoes),
  interacoes: num(r.interacoes),
  visitasPerfil: num(r.visitas_perfil),
  cliquesLink: num(r.cliques_link),
  publicacoes: num(r.publicacoes),
  demo: Boolean(r.demo),
})

const site = (r: Bruto = {}): SiteVindoDasRedes => ({ sessoes: num(r.sessoes), conversoes: num(r.conversoes) })

/** Quantas publicações o painel lista. */
const LIMITE = 20

/**
 * Tudo que o painel de redes precisa, em chamadas paralelas e já agregado no banco.
 * O GA4 não separa as sessões por rede aqui: o "vindo das redes" é sempre das três.
 */
export async function carregarSocial(f: FiltroSocial): Promise<DadosSocial> {
  const anterior = periodoAnterior(f.intervalo)
  const base = (i: Intervalo) => ({ p_inicio: i.inicio, p_fim: i.fim, p_marca: f.marcaId || null })
  const comRede = (i: Intervalo) => ({ ...base(i), p_rede: f.rede || null })
  const canal = (i: Intervalo) => ({ ...base(i), p_canal: 'Organic Social' })

  const [serie, atual, ant, siteAtual, siteAnt, posts] = await Promise.all([
    rpc('social_serie', comRede(f.intervalo)),
    rpc('social_totais', comRede(f.intervalo)),
    rpc('social_totais', comRede(anterior)),
    rpc('organico_totais', canal(f.intervalo)),
    rpc('organico_totais', canal(anterior)),
    rpc('social_publicacoes_top', { ...comRede(f.intervalo), p_limite: LIMITE }),
  ])

  return {
    serie: serie.map((r) => ({
      data: String(r.data),
      rede: r.rede as Rede,
      novosSeguidores: num(r.novos_seguidores),
      alcance: num(r.alcance),
      visualizacoes: num(r.visualizacoes),
      interacoes: num(r.interacoes),
      visitasPerfil: num(r.visitas_perfil),
      cliquesLink: num(r.cliques_link),
      demo: Boolean(r.demo),
    })),
    atual: totais(atual[0]),
    anterior: totais(ant[0]),
    site: site(siteAtual[0]),
    siteAnterior: site(siteAnt[0]),
    publicacoes: posts.map((r) => ({
      id: String(r.id),
      rede: r.rede as Rede,
      marcaId: String(r.marca_id),
      marca: String(r.marca),
      tipo: (r.tipo as string | null) ?? null,
      publicadaEm: String(r.publicada_em),
      legenda: (r.legenda as string | null) ?? null,
      link: (r.link as string | null) ?? null,
      miniatura: r.miniatura ? supabase.storage.from('miniaturas').getPublicUrl(String(r.miniatura)).data.publicUrl : null,
      visualizacoes: num(r.visualizacoes),
      interacoes: num(r.interacoes),
    })),
  }
}
